import { useEffect, useMemo, useRef, useState } from "react";
import { TRANSLATIONS } from "@/lib/translations";
import { adminListContent, adminSaveContentKey, adminUpdateSettings, adminUploadFile } from "@/lib/api";
import { useSiteContent } from "@/context/SiteContentContext";
import { useCatalog } from "@/context/CatalogContext";
import { isVideoUrl } from "@/lib/utils";

/**
 * Admin Content tab — edit every translation key + swap the hero banner
 * image. Overrides live in `content_overrides` (Mongo); when all three
 * language fields are cleared the row is deleted and the compiled default
 * kicks back in.
 */

// Human labels + colour for each key-prefix group
const GROUPS = [
  { prefix: "home.banner.", label: "Homepage banner", accent: "#F4CB47" },
  { prefix: "home.",         label: "Home — other",   accent: "#F9B4A5" },
  { prefix: "nav.",          label: "Navigation",     accent: "#DCE8A1" },
  { prefix: "cart.",         label: "Cart",           accent: "#F1DCC5" },
  { prefix: "checkout.",     label: "Checkout",       accent: "#F1DCC5" },
  { prefix: "order.",        label: "Order status",   accent: "#F9B4A5" },
  { prefix: "account.",      label: "Account",        accent: "#DCE8A1" },
  { prefix: "pdp.",          label: "Product page",   accent: "#F4CB47" },
  { prefix: "collection.",   label: "Collection",     accent: "#F1DCC5" },
  { prefix: "search.",       label: "Search",         accent: "#F1DCC5" },
  { prefix: "auth.",         label: "Auth modal",     accent: "#F9B4A5" },
  { prefix: "help.",         label: "Help & FAQ",     accent: "#DCE8A1" },
  { prefix: "journal.",      label: "Journal",        accent: "#F4CB47" },
  { prefix: "footer.",       label: "Footer",         accent: "#F1DCC5" },
  { prefix: "seo.",          label: "SEO — Google & browser tab", accent: "#F9B4A5" },
  { prefix: "badge.",        label: "Stock badges",   accent: "#F4CB47" },
  { prefix: "waitlist.",     label: "Waitlist / OOS", accent: "#DCE8A1" },
  { prefix: "",              label: "Other",          accent: "#E5E5E5" },  // catch-all
];

const groupForKey = (key) => GROUPS.find((g) => (g.prefix === "" ? true : key.startsWith(g.prefix)));

// ─────────────────────── Announcement bar block ──────────────────
// Marquee at the very top of the site — enable / disable, edit copy per
// language, and set the free-shipping threshold in one place. The same
// threshold drives Stripe checkout, the footer badge and the help page
// copy so changing it once ripples everywhere.

// Small row component for a language pair of announcement messages.
// Kept OUTSIDE `AnnouncementBarEditor` so React doesn't remount its
// <input>s on every keystroke (which was stealing focus in-between).
const AnnouncementRow = ({ label, v1, on1, v2, on2, ph1, ph2 }) => (
  <div className="grid grid-cols-2 gap-2 mb-2">
    <div>
      <div className="font-badge tracking-widest text-[10px] text-[#8A7568] mb-1">{label} · MSG 1</div>
      <input
        type="text"
        value={v1}
        onChange={(e) => on1(e.target.value)}
        placeholder={ph1}
        className="w-full border border-[#F1DCC5] rounded px-2 py-1.5 text-sm"
        data-testid={`admin-announcement-text-${label.toLowerCase()}-1`}
      />
    </div>
    <div>
      <div className="font-badge tracking-widest text-[10px] text-[#8A7568] mb-1">{label} · MSG 2</div>
      <input
        type="text"
        value={v2}
        onChange={(e) => on2(e.target.value)}
        placeholder={ph2}
        className="w-full border border-[#F1DCC5] rounded px-2 py-1.5 text-sm"
        data-testid={`admin-announcement-text-${label.toLowerCase()}-2`}
      />
    </div>
  </div>
);

const AnnouncementBarEditor = ({ initial, initialThreshold, onSaved }) => {
  const [enabled, setEnabled] = useState(Boolean(initial?.enabled));
  const [msg2Enabled, setMsg2Enabled] = useState(initial?.msg2Enabled !== false);
  // Two rotating messages per language (SKLUM style). Backwards-compatible:
  // if the admin previously saved a flat string we read it as message 1.
  const readMsg = (obj, idx) => {
    if (!obj) return "";
    if (typeof obj === "string") return idx === 0 ? obj : "";
    return obj[String(idx + 1)] ?? obj[`msg${idx + 1}`] ?? "";
  };
  const [en1, setEn1] = useState(readMsg(initial?.text?.en, 0));
  const [en2, setEn2] = useState(readMsg(initial?.text?.en, 1));
  const [pt1, setPt1] = useState(readMsg(initial?.text?.pt, 0));
  const [pt2, setPt2] = useState(readMsg(initial?.text?.pt, 1));
  const [es1, setEs1] = useState(readMsg(initial?.text?.es, 0));
  const [es2, setEs2] = useState(readMsg(initial?.text?.es, 1));
  const [threshold, setThreshold] = useState(initialThreshold ?? 150);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    setEnabled(Boolean(initial?.enabled));
    setMsg2Enabled(initial?.msg2Enabled !== false);
    setEn1(readMsg(initial?.text?.en, 0));
    setEn2(readMsg(initial?.text?.en, 1));
    setPt1(readMsg(initial?.text?.pt, 0));
    setPt2(readMsg(initial?.text?.pt, 1));
    setEs1(readMsg(initial?.text?.es, 0));
    setEs2(readMsg(initial?.text?.es, 1));
    setThreshold(initialThreshold ?? 150);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial?.enabled, initial?.msg2Enabled, JSON.stringify(initial?.text || {}), initialThreshold]);

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      const numeric = Number(threshold);
      await adminUpdateSettings({
        announcement_bar_enabled: enabled,
        announcement_bar_msg2_enabled: msg2Enabled,
        announcement_bar_text: {
          en: { 1: en1, 2: en2 },
          pt: { 1: pt1, 2: pt2 },
          es: { 1: es1, 2: es2 },
        },
        free_shipping_threshold: Number.isFinite(numeric) && numeric >= 0 ? numeric : 150,
      });
      setMsg("Saved ✓");
      onSaved?.();
    } catch (_e) {
      setMsg("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const previewNum = Number.isFinite(Number(threshold))
    ? (Number.isInteger(Number(threshold)) ? String(Number(threshold)) : Number(threshold).toFixed(2))
    : "—";
  const previewMsg1 = (en1 || "Free shipping on orders over €{threshold}").replaceAll("{threshold}", previewNum);
  const previewMsg2 = msg2Enabled ? (en2 || "").replaceAll("{threshold}", previewNum) : "";
  const previewCombined = [previewMsg1, previewMsg2].filter(Boolean).join(" ✦ ");


  return (
    <section
      className="bg-white border border-[#F1DCC5] rounded-2xl p-6 mb-8"
      data-testid="admin-announcement-editor"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-badge text-[10px] tracking-[0.22em] text-[var(--gl-orange-hover)]">TOP OF PAGE</div>
          <h2 className="font-serif-display text-2xl mt-1">Announcement bar &amp; free-shipping threshold</h2>
          <p className="text-xs text-[#8A7568] mt-1">
            Two rotating messages scroll across the top of every page (Message 2 is optional). Type <code className="bg-[#FFF3E6] px-1">{`{threshold}`}</code> anywhere to insert the € value below. The same number drives free-shipping in Stripe checkout, the footer badge and the help pages.
          </p>
        </div>
        {msg && <div className="text-xs text-[#4a7a2c]">{msg}</div>}
      </div>

      <div className="flex items-center gap-6 mb-4 flex-wrap">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            data-testid="admin-announcement-enabled"
          />
          Show the announcement bar
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={msg2Enabled}
            onChange={(e) => setMsg2Enabled(e.target.checked)}
            data-testid="admin-announcement-msg2-enabled"
          />
          Show <span className="font-semibold">Message 2</span> <span className="text-[#8A7568]">("Ships from Lisbon…")</span>
        </label>
      </div>

      <AnnouncementRow label="EN" v1={en1} on1={setEn1} v2={en2} on2={setEn2}
        ph1="Free shipping on orders over €{threshold}"
        ph2="Ships from Lisbon in 1–2 business days" />
      <AnnouncementRow label="PT" v1={pt1} on1={setPt1} v2={pt2} on2={setPt2}
        ph1="Envio grátis em encomendas acima de €{threshold}"
        ph2="Enviado de Lisboa em 1–2 dias úteis" />
      <AnnouncementRow label="ES" v1={es1} on1={setEs1} v2={es2} on2={setEs2}
        ph1="Envío gratis en pedidos superiores a €{threshold}"
        ph2="Enviado desde Lisboa en 1–2 días hábiles" />

      <div className="flex items-end gap-3 mt-3 mb-4">
        <label className="text-xs">
          <div className="font-badge tracking-widest text-[#8A7568] mb-1">FREE-SHIPPING THRESHOLD (€)</div>
          <input
            type="number"
            min="0"
            step="1"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            className="w-32 border border-[#F1DCC5] rounded px-2 py-1.5 text-sm"
            data-testid="admin-announcement-threshold"
          />
        </label>
        <div className="text-xs text-[#8A7568] pb-2">Applies to every checkout, the marquee, the footer badge and the shipping help page.</div>
      </div>

      <div className="mb-4 bg-[#241C18] text-[#F1ECE2] rounded overflow-hidden py-1.5 px-4">
        <div className="text-[10px] font-badge tracking-[0.22em] uppercase truncate" data-testid="admin-announcement-preview">{previewCombined}</div>
      </div>

      <button
        type="button"
        onClick={save}
        disabled={saving}
        data-testid="admin-announcement-save"
        className="px-4 py-2 bg-[#241C18] text-[var(--gl-orange)] rounded text-sm"
      >
        {saving ? "Saving…" : "Save announcement bar"}
      </button>
    </section>
  );
};

// ─────────────────────── Shipping countries editor ─────────────
// Admin picks which countries Stripe's shipping_address_collection will
// allow at checkout. Empty selection auto-defaults to Portugal + Spain
// so we can never accidentally lock every buyer out. The full list of
// pickable ISO codes ships from `/api/content` → settings.supported_shipping_countries
// so we don't duplicate the whitelist on the frontend.

const COUNTRY_NAMES = {
  PT: "Portugal", ES: "Spain", FR: "France", DE: "Germany", IT: "Italy",
  NL: "Netherlands", BE: "Belgium", IE: "Ireland", AT: "Austria", GR: "Greece",
  PL: "Poland", SE: "Sweden", DK: "Denmark", FI: "Finland", CZ: "Czechia",
  SK: "Slovakia", HU: "Hungary", RO: "Romania", BG: "Bulgaria", HR: "Croatia",
  SI: "Slovenia", EE: "Estonia", LV: "Latvia", LT: "Lithuania", LU: "Luxembourg",
  MT: "Malta", CY: "Cyprus", GB: "United Kingdom", US: "United States",
  CA: "Canada", AU: "Australia", NZ: "New Zealand", CH: "Switzerland",
  NO: "Norway", BR: "Brazil", MX: "Mexico", JP: "Japan", SG: "Singapore",
  AE: "UAE",
};

const ShippingCountriesEditor = ({ initialSelected, supported, onSaved }) => {
  const [selected, setSelected] = useState(new Set(initialSelected || ["PT", "ES"]));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    setSelected(new Set(initialSelected && initialSelected.length ? initialSelected : ["PT", "ES"]));
  }, [JSON.stringify(initialSelected)]);

  const toggle = (code) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
    setMsg("");
  };

  const setPreset = (codes) => {
    setSelected(new Set(codes));
    setMsg("");
  };

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      await adminUpdateSettings({ allowed_shipping_countries: Array.from(selected) });
      setMsg("Saved ✓");
      onSaved?.();
    } catch (_e) {
      setMsg("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const list = (supported && supported.length ? supported : Object.keys(COUNTRY_NAMES));
  const EU_CODES = ["PT", "ES", "FR", "DE", "IT", "NL", "BE", "IE", "AT", "GR", "PL", "SE", "DK", "FI", "CZ", "SK", "HU", "RO", "BG", "HR", "SI", "EE", "LV", "LT", "LU", "MT", "CY"];

  return (
    <section
      className="bg-white border border-[#F1DCC5] rounded-2xl p-6 mb-8"
      data-testid="admin-shipping-countries-editor"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-badge text-[10px] tracking-[0.22em] text-[var(--gl-orange-hover)]">CHECKOUT · SHIPPING DESTINATIONS</div>
          <h2 className="font-serif-display text-2xl mt-1">Where GOYA LISBOA ships to</h2>
          <p className="text-xs text-[#8A7568] mt-1">
            Tick the countries you'll accept orders from. Stripe checkout will only offer shipping addresses in these countries. Leaving everything unticked reverts to Portugal + Spain by default.
          </p>
        </div>
        {msg && <div className="text-xs text-[#4a7a2c]">{msg}</div>}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <button
          type="button"
          onClick={() => setPreset(["PT", "ES"])}
          className="px-3 py-1.5 border border-[#241C18] rounded-full text-xs"
          data-testid="admin-shipping-preset-iberia"
        >
          Iberia only (PT + ES)
        </button>
        <button
          type="button"
          onClick={() => setPreset(EU_CODES)}
          className="px-3 py-1.5 border border-[#241C18] rounded-full text-xs"
          data-testid="admin-shipping-preset-eu"
        >
          All EU
        </button>
        <button
          type="button"
          onClick={() => setPreset(list)}
          className="px-3 py-1.5 border border-[#241C18] rounded-full text-xs"
          data-testid="admin-shipping-preset-worldwide"
        >
          Worldwide
        </button>
        <button
          type="button"
          onClick={() => setPreset([])}
          className="px-3 py-1.5 border border-[#8A7568] text-[#8A7568] rounded-full text-xs"
          data-testid="admin-shipping-preset-clear"
        >
          Clear all
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5 mb-4">
        {list.map((code) => {
          const on = selected.has(code);
          return (
            <label
              key={code}
              className={`flex items-center gap-2 px-2.5 py-1.5 rounded border text-sm cursor-pointer transition ${
                on
                  ? "border-[var(--gl-orange)] bg-[#FFF3E6]"
                  : "border-[#F1DCC5] hover:border-[#241C18]"
              }`}
              data-testid={`admin-shipping-country-${code}`}
            >
              <input
                type="checkbox"
                checked={on}
                onChange={() => toggle(code)}
                className="accent-[var(--gl-orange)]"
              />
              <span className="font-mono text-[10px] text-[#8A7568] w-6">{code}</span>
              <span className="truncate">{COUNTRY_NAMES[code] || code}</span>
            </label>
          );
        })}
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="admin-shipping-countries-save"
          className="px-4 py-2 bg-[#241C18] text-[var(--gl-orange)] rounded text-sm"
        >
          {saving ? "Saving…" : "Save shipping countries"}
        </button>
        <div className="text-xs text-[#8A7568]" data-testid="admin-shipping-count">
          {selected.size} {selected.size === 1 ? "country" : "countries"} enabled
        </div>
      </div>
    </section>
  );
};

// ─────────────────────── Storefront switches ─────────────────

const StorefrontSwitchesEditor = ({ initialShowStockLeft, initialThreshold, initialTrendingEnabled, onSaved }) => {
  const [showStockLeft, setShowStockLeft] = useState(initialShowStockLeft !== false);
  const [threshold, setThreshold] = useState(Number(initialThreshold) || 3);
  const [trendingEnabled, setTrendingEnabled] = useState(initialTrendingEnabled !== false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    setShowStockLeft(initialShowStockLeft !== false);
    setThreshold(Number(initialThreshold) || 3);
    setTrendingEnabled(initialTrendingEnabled !== false);
  }, [initialShowStockLeft, initialThreshold, initialTrendingEnabled]);

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      await adminUpdateSettings({
        show_stock_left: showStockLeft,
        stock_left_threshold: Math.max(1, Math.min(20, Number(threshold) || 3)),
        trending_enabled: trendingEnabled,
      });
      setMsg("Saved ✓");
      onSaved?.();
    } catch (_e) {
      setMsg("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-white border border-[#F1DCC5] rounded-2xl p-6 mb-8" data-testid="admin-storefront-switches">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-badge text-[10px] tracking-[0.22em] text-[var(--gl-orange-hover)]">STOREFRONT SWITCHES</div>
          <h2 className="font-serif-display text-2xl mt-1">Scarcity indicators</h2>
          <p className="text-xs text-[#8A7568] mt-1">
            Controls the <em>Only N left</em> chip on cards + PDP and the per-variant counts inside the size chips (<em>M / 38 · 1 left</em>).
          </p>
        </div>
        {msg && <div className="text-xs text-[#4a7a2c]">{msg}</div>}
      </div>

      <label className="flex items-center gap-3 mb-4 cursor-pointer">
        <input
          type="checkbox"
          checked={showStockLeft}
          onChange={(e) => setShowStockLeft(e.target.checked)}
          data-testid="admin-show-stock-left-toggle"
          className="h-5 w-5 accent-[var(--gl-orange)]"
        />
        <span className="text-sm text-[#241C18]">
          {showStockLeft
            ? "Scarcity indicators visible on storefront."
            : "Scarcity indicators hidden — shoppers won't see low-stock counts."}
        </span>
      </label>

      <label className={`block ${showStockLeft ? "" : "opacity-50"}`}>
        <span className="font-badge text-[10px] tracking-[0.22em] text-[#8A7568]">
          SHOW SCARCITY WHEN TOTAL LEFT ≤ <span className="text-[#241C18]">{threshold}</span>
        </span>
        <input
          type="range"
          min="1"
          max="20"
          step="1"
          value={threshold}
          disabled={!showStockLeft}
          onChange={(e) => setThreshold(Number(e.target.value))}
          data-testid="admin-stock-left-threshold"
          className="w-full mt-1 accent-[var(--gl-orange)]"
        />
        <div className="flex justify-between text-[10px] text-[#8A7568] mt-0.5">
          <span>1 piece</span>
          <span>20 pieces</span>
        </div>
        <p className="text-[11px] text-[#8A7568] mt-1">
          Product appears "in demand" when the sum of stock across all its variants is ≤ this number.
        </p>
      </label>

      <div className="border-t border-[#F1DCC5] mt-5 pt-5">
        <div className="font-badge text-[10px] tracking-[0.22em] text-[var(--gl-orange-hover)] mb-2">TRENDING SURFACE</div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={trendingEnabled}
            onChange={(e) => setTrendingEnabled(e.target.checked)}
            data-testid="admin-trending-enabled-toggle"
            className="h-5 w-5 accent-[var(--gl-orange)]"
          />
          <span className="text-sm text-[#241C18]">
            {trendingEnabled
              ? "Trending enabled — the TRENDING pill, home trending row and /collection/trending filter are live."
              : "Trending disabled sitewide — no product shows the TRENDING pill, and /collection/trending returns an empty state."}
          </span>
        </label>
        <p className="text-[11px] text-[#8A7568] mt-2 pl-8">
          Hard sitewide kill switch. Overrides per-product trending flags for both storefront and feed outputs.
        </p>
      </div>

      <button
        type="button"
        onClick={save}
        disabled={saving}
        data-testid="admin-show-stock-left-save"
        className="mt-4 btn-orange text-xs disabled:opacity-40"
      >
        {saving ? "Saving…" : "Save"}
      </button>
    </section>
  );
};


// ─────────────────────── Maintenance mode toggle ─────────────────

const MaintenanceEditor = ({ initial, initialMessage, onSaved }) => {
  const [enabled, setEnabled] = useState(Boolean(initial));
  const [message, setMessage] = useState({
    en: initialMessage?.en || "",
    pt: initialMessage?.pt || "",
    es: initialMessage?.es || "",
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    setEnabled(Boolean(initial));
    setMessage({
      en: initialMessage?.en || "",
      pt: initialMessage?.pt || "",
      es: initialMessage?.es || "",
    });
  }, [initial, initialMessage]);

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      await adminUpdateSettings({
        maintenance_mode: enabled,
        maintenance_message: message,
      });
      setMsg("Saved ✓");
      onSaved?.();
    } catch (_e) {
      setMsg("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-white border border-[#F1DCC5] rounded-2xl p-6 mb-8" data-testid="admin-maintenance-editor">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-badge text-[10px] tracking-[0.22em] text-[var(--gl-orange-hover)]">SITE STATUS</div>
          <h2 className="font-serif-display text-2xl mt-1">Maintenance mode</h2>
          <p className="text-xs text-[#8A7568] mt-1">
            When ON, the storefront shows a full-page maintenance overlay to visitors. <span className="text-[#241C18] font-medium">/admin stays fully accessible.</span>
          </p>
        </div>
        {msg && <div className="text-xs text-[#4a7a2c]">{msg}</div>}
      </div>

      <label className="flex items-center gap-3 mb-4 cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          data-testid="admin-maintenance-toggle"
          className="h-5 w-5 accent-[var(--gl-orange)]"
        />
        <span className="text-sm text-[#241C18]">
          {enabled ? "Site is in maintenance mode — customers see the overlay." : "Site is live — everyone sees the storefront normally."}
        </span>
      </label>

      <div className="grid md:grid-cols-3 gap-3">
        {["en", "pt", "es"].map((lc) => (
          <label key={lc} className="block">
            <span className="font-badge text-[10px] tracking-[0.22em] text-[#8A7568]">MESSAGE · {lc.toUpperCase()}</span>
            <textarea
              rows={3}
              value={message[lc]}
              onChange={(e) => setMessage({ ...message, [lc]: e.target.value })}
              placeholder={
                lc === "pt" ? "Voltamos em breve — estamos a preparar novidades." :
                lc === "es" ? "Volvemos pronto — estamos preparando novedades." :
                "We'll be back shortly — brewing something new."
              }
              className="w-full mt-1 px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm resize-none"
            />
          </label>
        ))}
      </div>

      <button
        type="button"
        onClick={save}
        disabled={saving}
        data-testid="admin-maintenance-save"
        className="mt-4 btn-orange text-xs disabled:opacity-40"
      >
        {saving ? "Saving…" : "Save maintenance settings"}
      </button>
    </section>
  );
};


// ─────────────────────── Hero banner block ───────────────────────

const HeroBannerEditor = ({ initialImageUrl, initialPos, initialPosMobile, initialScale, initialScaleMobile, initialTitleScale, initialTitleColor, initialCtaColor, initialSubtitleEnabled, initialCollectionTitleEnabled, onSaved }) => {
  const [imageUrl, setImageUrl] = useState(initialImageUrl || "");
  const [pos, setPos] = useState(initialPos || "40% 45%");
  const [posMobile, setPosMobile] = useState(initialPosMobile || "75% 45%");
  const [imgScale, setImgScale] = useState(Number(initialScale) || 1.0);
  const [imgScaleMobile, setImgScaleMobile] = useState(Number(initialScaleMobile) || 1.0);
  // Slider 0.4 – 1.6, 0.05 steps. Default 1.0 matches the compiled hero size.
  const [titleScale, setTitleScale] = useState(Number(initialTitleScale) || 1.0);
  const [titleColor, setTitleColor] = useState(initialTitleColor || "");
  const [ctaColor, setCtaColor] = useState(initialCtaColor || "");
  const [subtitleEnabled, setSubtitleEnabled] = useState(initialSubtitleEnabled !== false);
  const [collectionTitleEnabled, setCollectionTitleEnabled] = useState(initialCollectionTitleEnabled !== false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    setImageUrl(initialImageUrl || "");
    setPos(initialPos || "40% 45%");
    setPosMobile(initialPosMobile || "75% 45%");
    setImgScale(Number(initialScale) || 1.0);
    setImgScaleMobile(Number(initialScaleMobile) || 1.0);
    setTitleScale(Number(initialTitleScale) || 1.0);
    setTitleColor(initialTitleColor || "");
    setCtaColor(initialCtaColor || "");
    setSubtitleEnabled(initialSubtitleEnabled !== false);
    setCollectionTitleEnabled(initialCollectionTitleEnabled !== false);
  }, [initialImageUrl, initialPos, initialPosMobile, initialScale, initialScaleMobile, initialTitleScale, initialTitleColor, initialCtaColor, initialSubtitleEnabled, initialCollectionTitleEnabled]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setMsg("");
    try {
      const res = await adminUploadFile(file);
      setImageUrl(res.url);
      setMsg("Uploaded ✓ — remember to Save");
    } catch (_e) {
      setMsg("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      await adminUpdateSettings({
        hero_image_url: imageUrl || null,
        hero_image_position: pos || "40% 45%",
        hero_image_position_mobile: posMobile || "75% 45%",
        hero_image_scale: Number(imgScale) || 1.0,
        hero_image_scale_mobile: Number(imgScaleMobile) || 1.0,
        hero_title_scale: Number(titleScale) || 1.0,
        hero_title_color: titleColor || "",
        hero_cta_color: ctaColor || "",
        hero_subtitle_enabled: subtitleEnabled,
        collection_title_enabled: collectionTitleEnabled,
      });
      setMsg("Saved ✓");
      onSaved?.();
    } catch (_e) {
      setMsg("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-white border border-[#F1DCC5] rounded-2xl p-6 mb-8" data-testid="admin-hero-editor">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-badge text-[10px] tracking-[0.22em] text-[var(--gl-orange-hover)]">HOMEPAGE HERO</div>
          <h2 className="font-serif-display text-2xl mt-1">Banner image</h2>
          <p className="text-xs text-[#8A7568] mt-1">
            Text (Summer / Sale / subtitle / CTA) is edited below — search for <code className="bg-[#FFE9D6] px-1 rounded">home.banner</code>.
          </p>
        </div>
        {msg && <div className="text-xs text-[#4a7a2c]">{msg}</div>}
      </div>

      <div className="grid md:grid-cols-[1fr] gap-6 items-start">
        <div className="space-y-3">
          <label className="block">
            <span className="font-badge text-[10px] tracking-[0.22em] text-[#8A7568]">IMAGE URL</span>
            <input
              data-testid="admin-hero-image-url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://…"
              className="w-full mt-1 px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm"
            />
          </label>
          <div className="flex items-center gap-3">
            <input
              ref={fileRef}
              data-testid="admin-hero-file"
              type="file"
              accept="image/*"
              onChange={(e) => upload(e.target.files?.[0])}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="text-xs underline underline-offset-2 disabled:opacity-40"
            >
              {uploading ? "Uploading…" : "or upload a new image →"}
            </button>
          </div>

          {/* Desktop + Mobile preview blocks — each with focal point + zoom.
              Previews mirror the live hero crop ratios so what you see here
              is what shoppers see at that breakpoint. */}
          <div className="grid md:grid-cols-2 gap-5 pt-2">
            {/* ── DESKTOP ── */}
            <div className="space-y-2" data-testid="admin-hero-desktop-block">
              <div className="font-badge text-[10px] tracking-[0.22em] text-[var(--gl-orange-hover)]">DESKTOP VIEW</div>
              <div className="aspect-[21/9] w-full overflow-hidden bg-[#FFE9D6] rounded" data-testid="admin-hero-preview-desktop">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt="hero desktop preview"
                    style={{ objectPosition: pos, transform: `scale(${imgScale})`, transformOrigin: pos }}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#8A7568] text-xs">No image set</div>
                )}
              </div>
              <label className="block">
                <span className="font-badge text-[10px] tracking-[0.22em] text-[#8A7568]">FOCAL POINT (desktop)</span>
                <input
                  data-testid="admin-hero-position"
                  value={pos}
                  onChange={(e) => setPos(e.target.value)}
                  placeholder="40% 45%"
                  className="w-full mt-1 px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm"
                />
                <span className="text-[10px] text-[#8A7568] block mt-1">
                  e.g. <code className="bg-[#FFE9D6] px-1 rounded">50% 50%</code> = centre
                </span>
              </label>
              <label className="block">
                <span className="font-badge text-[10px] tracking-[0.22em] text-[#8A7568]">
                  ZOOM (desktop) — <span className="text-[#241C18]">{Math.round(Number(imgScale) * 100)}%</span>
                </span>
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.05"
                  value={imgScale}
                  onChange={(e) => setImgScale(Number(e.target.value))}
                  data-testid="admin-hero-scale"
                  className="w-full mt-1 accent-[var(--gl-orange)]"
                />
                <div className="flex justify-between text-[10px] text-[#8A7568]">
                  <span>100%</span>
                  <button type="button" onClick={() => setImgScale(1.0)} className="underline underline-offset-2 hover:text-[#241C18]">Reset</button>
                  <span>300%</span>
                </div>
              </label>
            </div>

            {/* ── MOBILE ── */}
            <div className="space-y-2" data-testid="admin-hero-mobile-block">
              <div className="font-badge text-[10px] tracking-[0.22em] text-[var(--gl-orange-hover)]">MOBILE VIEW</div>
              <div className="mx-auto w-[200px] aspect-[4/5] overflow-hidden bg-[#FFE9D6] rounded" data-testid="admin-hero-preview-mobile">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt="hero mobile preview"
                    style={{ objectPosition: posMobile, transform: `scale(${imgScaleMobile})`, transformOrigin: posMobile }}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#8A7568] text-xs">No image set</div>
                )}
              </div>
              <label className="block">
                <span className="font-badge text-[10px] tracking-[0.22em] text-[#8A7568]">FOCAL POINT (mobile)</span>
                <input
                  data-testid="admin-hero-position-mobile"
                  value={posMobile}
                  onChange={(e) => setPosMobile(e.target.value)}
                  placeholder="75% 45%"
                  className="w-full mt-1 px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm"
                />
                <span className="text-[10px] text-[#8A7568] block mt-1">
                  Narrow crop — shift X right to keep subjects in frame.
                </span>
              </label>
              <label className="block">
                <span className="font-badge text-[10px] tracking-[0.22em] text-[#8A7568]">
                  ZOOM (mobile) — <span className="text-[#241C18]">{Math.round(Number(imgScaleMobile) * 100)}%</span>
                </span>
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.05"
                  value={imgScaleMobile}
                  onChange={(e) => setImgScaleMobile(Number(e.target.value))}
                  data-testid="admin-hero-scale-mobile"
                  className="w-full mt-1 accent-[var(--gl-orange)]"
                />
                <div className="flex justify-between text-[10px] text-[#8A7568]">
                  <span>100%</span>
                  <button type="button" onClick={() => setImgScaleMobile(1.0)} className="underline underline-offset-2 hover:text-[#241C18]">Reset</button>
                  <span>300%</span>
                </div>
              </label>
            </div>
          </div>
          <label className="block">
            <span className="font-badge text-[10px] tracking-[0.22em] text-[#8A7568]">
              TITLE SIZE — <span className="text-[#241C18]">{Math.round(Number(titleScale) * 100)}%</span>
            </span>
            <input
              type="range"
              min="0.4"
              max="1.6"
              step="0.05"
              value={titleScale}
              onChange={(e) => setTitleScale(Number(e.target.value))}
              data-testid="admin-hero-title-scale"
              className="w-full mt-1 accent-[var(--gl-orange)]"
            />
            <div className="flex justify-between text-[10px] text-[#8A7568] mt-0.5">
              <span>Smaller</span>
              <button
                type="button"
                onClick={() => setTitleScale(1.0)}
                className="underline underline-offset-2 hover:text-[#241C18]"
              >
                Reset to 100%
              </button>
              <span>Bigger</span>
            </div>
          </label>
          <label className="block">
            <span className="font-badge text-[10px] tracking-[0.22em] text-[#8A7568]">
              TITLE COLOR{" "}
              {titleColor ? (
                <span className="text-[#241C18]">— {titleColor}</span>
              ) : (
                <span className="text-[#241C18]">— default (yellow)</span>
              )}
            </span>
            <div className="mt-1 flex items-center gap-3">
              <input
                type="color"
                value={titleColor || "#F5B840"}
                onChange={(e) => setTitleColor(e.target.value)}
                data-testid="admin-hero-title-color"
                className="h-9 w-14 rounded cursor-pointer border border-[#F1DCC5] bg-white"
              />
              <input
                type="text"
                value={titleColor}
                onChange={(e) => setTitleColor(e.target.value)}
                placeholder="#RRGGBB"
                data-testid="admin-hero-title-color-hex"
                className="w-32 px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm"
              />
              {titleColor && (
                <button
                  type="button"
                  onClick={() => setTitleColor("")}
                  className="text-xs underline underline-offset-2 text-[#8A7568] hover:text-[#241C18]"
                >
                  Reset to default
                </button>
              )}
            </div>
          </label>
          <label className="block">
            <span className="font-badge text-[10px] tracking-[0.22em] text-[#8A7568]">
              CTA COLOR (Shop now text + underline + arrow){" "}
              {ctaColor ? (
                <span className="text-[#241C18]">— {ctaColor}</span>
              ) : (
                <span className="text-[#241C18]">— default (yellow)</span>
              )}
            </span>
            <div className="mt-1 flex items-center gap-3">
              <input
                type="color"
                value={ctaColor || "#F5B840"}
                onChange={(e) => setCtaColor(e.target.value)}
                data-testid="admin-hero-cta-color"
                className="h-9 w-14 rounded cursor-pointer border border-[#F1DCC5] bg-white"
              />
              <input
                type="text"
                value={ctaColor}
                onChange={(e) => setCtaColor(e.target.value)}
                placeholder="#RRGGBB"
                data-testid="admin-hero-cta-color-hex"
                className="w-32 px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm"
              />
              {ctaColor && (
                <button
                  type="button"
                  onClick={() => setCtaColor("")}
                  className="text-xs underline underline-offset-2 text-[#8A7568] hover:text-[#241C18]"
                >
                  Reset to default
                </button>
              )}
            </div>
          </label>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={subtitleEnabled}
              onChange={(e) => setSubtitleEnabled(e.target.checked)}
              data-testid="admin-hero-subtitle-enabled"
              className="w-4 h-4 accent-[var(--gl-orange)]"
            />
            <span className="text-sm text-[#241C18]">
              Show hero subtitle
              <span className="ml-2 text-xs text-[#8A7568]">(the small line under the hero title)</span>
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={collectionTitleEnabled}
              onChange={(e) => setCollectionTitleEnabled(e.target.checked)}
              data-testid="admin-collection-title-enabled"
              className="w-4 h-4 accent-[var(--gl-orange)]"
            />
            <span className="text-sm text-[#241C18]">
              Show homepage "Pick your vibe" heading
              <span className="ml-2 text-xs text-[#8A7568]">(above the category grid)</span>
            </span>
          </label>
          <button
            type="button"
            data-testid="admin-hero-save"
            onClick={save}
            disabled={saving}
            className="btn-orange text-xs disabled:opacity-40"
          >
            {saving ? "Saving…" : "Save banner"}
          </button>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────── Instagram gallery editor ─────────────────

const InstagramGalleryEditor = ({ initial, onSaved }) => {
  const [urls, setUrls] = useState(initial || []);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { setUrls(initial || []); }, [initial]);

  const addUrl = (url) => {
    const u = (url || "").trim();
    if (!u) return;
    if (urls.includes(u)) return;
    setUrls([...urls, u]);
    setMsg("");
  };

  const remove = (i) => setUrls(urls.filter((_, idx) => idx !== i));

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= urls.length) return;
    const copy = [...urls];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setUrls(copy);
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setMsg("");
    try {
      const res = await adminUploadFile(file);
      addUrl(res.url);
    } catch (_e) {
      setMsg("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      await adminUpdateSettings({ instagram_gallery: urls });
      setMsg("Saved ✓");
      onSaved?.();
    } catch (_e) {
      setMsg("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-white border border-[#F1DCC5] rounded-2xl p-6 mb-8" data-testid="admin-instagram-editor">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-badge text-[10px] tracking-[0.22em] text-[var(--gl-orange-hover)]">HOMEPAGE #GOYAGIRLS</div>
          <h2 className="font-serif-display text-2xl mt-1">Instagram gallery</h2>
          <p className="text-xs text-[#8A7568] mt-1">
            First item becomes the large 2×2 hero tile. Mix images and short videos (mp4/webm/mov). Add as many as you like — the storefront renders them all. Leave empty to auto-fill with product photos.
          </p>
        </div>
        {msg && <div className="text-xs text-[#4a7a2c]">{msg}</div>}
      </div>

      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4" data-testid="admin-instagram-thumbs">
        {urls.map((u, i) => {
          const isVideo = isVideoUrl(u);
          return (
          <div key={u + i} className="relative group aspect-square bg-[#FFE9D6] rounded overflow-hidden">
            {isVideo ? (
              <video
                src={u}
                muted
                loop
                playsInline
                autoPlay
                className="w-full h-full object-cover pointer-events-none"
              />
            ) : (
              <img src={u} alt="" className="w-full h-full object-cover" />
            )}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2 text-white text-xs">
              <button onClick={() => move(i, -1)} disabled={i === 0} title="Move left" className="px-1.5 py-0.5 bg-white/20 rounded disabled:opacity-30">←</button>
              <button onClick={() => move(i, 1)} disabled={i === urls.length - 1} title="Move right" className="px-1.5 py-0.5 bg-white/20 rounded disabled:opacity-30">→</button>
              <button onClick={() => remove(i)} title="Remove" className="px-1.5 py-0.5 bg-red-500/80 rounded" data-testid={`ig-remove-${i}`}>✕</button>
            </div>
            {i === 0 && <div className="absolute top-1 left-1 bg-[var(--gl-orange)] text-[9px] font-badge tracking-widest px-1.5 py-0.5 rounded">HERO</div>}
            {isVideo && <div className="absolute bottom-1 right-1 bg-[#241C18]/80 text-white text-[9px] font-badge tracking-widest px-1.5 py-0.5 rounded">VIDEO</div>}
          </div>
          );
        })}
        {urls.length === 0 && (
          <div className="col-span-3 md:col-span-6 py-8 text-center text-xs text-[#8A7568]">No media configured — using product photos.</div>
        )}
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <input
          type="text"
          placeholder="Paste an image or video URL…"
          data-testid="admin-instagram-url-input"
          className="flex-1 min-w-[240px] px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm"
          onKeyDown={(e) => {
            if (e.key === "Enter") { addUrl(e.currentTarget.value); e.currentTarget.value = ""; }
          }}
        />
        <input ref={fileRef} type="file" accept="image/*,video/*" hidden onChange={(e) => uploadFile(e.target.files?.[0])} />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="px-4 py-2 border border-[#241C18] rounded text-sm"
          data-testid="admin-instagram-upload"
        >
          {uploading ? "Uploading…" : "Upload image or video"}
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="admin-instagram-save"
          className="px-4 py-2 bg-[#241C18] text-[var(--gl-orange)] rounded text-sm"
        >
          {saving ? "Saving…" : "Save gallery"}
        </button>
      </div>
    </section>
  );
};

// ─────────────────────── Category tile images editor ─────────────
// Admin can swap the image used for each homepage category tile.
// Two options per row: pick from any existing product photo (dropdown
// gallery) or upload a new file. Storage is `site_settings.category_images`
// keyed by category slug.

const CategoryImagesEditor = ({ initial, onSaved }) => {
  const [map, setMap] = useState(initial || {});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [pickerFor, setPickerFor] = useState(null); // slug the picker is open for
  const [uploadingFor, setUploadingFor] = useState(null);
  const fileRefs = useRef({});
  const { products, categories } = useCatalog();
  const labelOf = (slug) => categories.find((c) => c.slug === slug)?.name_pt || slug;

  useEffect(() => { setMap(initial || {}); }, [initial]);

  // Collect the product photos we can offer as picks, grouped by category
  // so the picker only shows relevant options first.
  const photosByCategory = useMemo(() => {
    const out = new Map();
    for (const p of products || []) {
      if (!p.images?.length) continue;
      const list = out.get(p.category) || [];
      for (const url of p.images) {
        if (url && !list.includes(url)) list.push(url);
      }
      out.set(p.category, list);
    }
    return out;
  }, [products]);

  const categorySlugs = useMemo(() => {
    // Show every category that has ≥1 product, plus any slug already
    // in the override map (in case the admin configured one that later
    // became empty of products).
    const set = new Set([
      ...(products || []).map((p) => p.category),
      ...Object.keys(map || {}),
    ]);
    return categories.map((c) => c.slug).filter((s) => set.has(s));
  }, [products, map, categories]);

  const setForSlug = (slug, url) => {
    setMap((m) => {
      const next = { ...m };
      if (url) next[slug] = url;
      else delete next[slug];
      return next;
    });
    setMsg("");
  };

  const uploadFor = async (slug, file) => {
    if (!file) return;
    setUploadingFor(slug);
    setMsg("");
    try {
      const res = await adminUploadFile(file);
      setForSlug(slug, res.url);
    } catch (_e) {
      setMsg("Upload failed");
    } finally {
      setUploadingFor(null);
    }
  };

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      await adminUpdateSettings({ category_images: map });
      setMsg("Saved ✓");
      onSaved?.();
    } catch (_e) {
      setMsg("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="bg-white border border-[#F1DCC5] rounded-2xl p-6 mb-8"
      data-testid="admin-category-images-editor"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-badge text-[10px] tracking-[0.22em] text-[var(--gl-orange-hover)]">HOMEPAGE CATEGORY TILES</div>
          <h2 className="font-serif-display text-2xl mt-1">Category tile images</h2>
          <p className="text-xs text-[#8A7568] mt-1">
            Pick from any product photo or upload a new one. Leave a row cleared to fall back to the first product image of that category.
          </p>
        </div>
        {msg && <div className="text-xs text-[#4a7a2c]">{msg}</div>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {categorySlugs.length === 0 && (
          <div className="col-span-2 py-6 text-center text-xs text-[#8A7568]">Add products first — categories show up here automatically.</div>
        )}
        {categorySlugs.map((slug) => {
          const current = map[slug];
          const fallback = photosByCategory.get(slug)?.[0];
          const preview = current || fallback;
          const isPickerOpen = pickerFor === slug;
          const isUploading = uploadingFor === slug;

          return (
            <div key={slug} className="border border-[#F1DCC5] rounded-lg p-3" data-testid={`category-row-${slug}`}>
              <div className="flex items-start gap-3">
                <div className="w-20 h-24 shrink-0 bg-[#FFE9D6] rounded overflow-hidden">
                  {preview ? (
                    <img src={preview} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[10px] text-[#8A7568]">no image</div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-badge text-[11px] tracking-[0.14em] text-[#241C18]">{labelOf(slug)}</div>
                  <div className="text-[11px] text-[#8A7568] mt-0.5 truncate">
                    {current ? "Custom image" : fallback ? "Auto (product photo)" : "No image available"}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setPickerFor(isPickerOpen ? null : slug)}
                      className="px-3 py-1.5 border border-[#241C18] rounded text-xs"
                      data-testid={`category-pick-${slug}`}
                    >
                      {isPickerOpen ? "Cancel" : "Pick from product photos"}
                    </button>
                    <input
                      ref={(el) => (fileRefs.current[slug] = el)}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => uploadFor(slug, e.target.files?.[0])}
                    />
                    <button
                      type="button"
                      onClick={() => fileRefs.current[slug]?.click()}
                      className="px-3 py-1.5 border border-[#241C18] rounded text-xs"
                      data-testid={`category-upload-${slug}`}
                    >
                      {isUploading ? "Uploading…" : "Upload image"}
                    </button>
                    {current && (
                      <button
                        type="button"
                        onClick={() => setForSlug(slug, null)}
                        className="px-3 py-1.5 text-xs text-[#a1543d] underline"
                        data-testid={`category-reset-${slug}`}
                      >
                        Reset to auto
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {isPickerOpen && (
                <div className="mt-3 pt-3 border-t border-[#F1DCC5]">
                  <div className="text-[10px] font-badge tracking-widest text-[#8A7568] mb-2">Product photos in this category</div>
                  <div className="grid grid-cols-4 md:grid-cols-6 gap-2 max-h-64 overflow-y-auto pr-1">
                    {(photosByCategory.get(slug) || []).map((url) => (
                      <button
                        type="button"
                        key={url}
                        onClick={() => { setForSlug(slug, url); setPickerFor(null); }}
                        className={`aspect-square rounded overflow-hidden border-2 transition ${
                          url === current ? "border-[var(--gl-orange)]" : "border-transparent hover:border-[#241C18]"
                        }`}
                        data-testid={`category-photo-option-${slug}`}
                      >
                        <img src={url} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                    {(photosByCategory.get(slug) || []).length === 0 && (
                      <div className="col-span-4 md:col-span-6 py-4 text-center text-xs text-[#8A7568]">No product photos yet in this category — upload one instead.</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={save}
        disabled={saving}
        data-testid="admin-category-images-save"
        className="px-4 py-2 bg-[#241C18] text-[var(--gl-orange)] rounded text-sm"
      >
        {saving ? "Saving…" : "Save category images"}
      </button>
    </section>
  );
};

// ─────────────────────── Per-key editor row ──────────────────────

const KeyRow = ({ k, defaults, override, onSaved }) => {
  // Compute the current value shown: override if present, else compiled default.
  const [en, setEn] = useState(override?.en ?? defaults.en ?? "");
  const [pt, setPt] = useState(override?.pt ?? defaults.pt ?? "");
  const [es, setEs] = useState(override?.es ?? defaults.es ?? "");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState("");

  // Only re-sync from props when the incoming external values actually
  // differ AND the user hasn't started typing. This is critical: the
  // parent re-renders every time ANY row is saved (new `overrides` object
  // identity, even if this row's values are identical). Without the guard
  // the reset would wipe any in-progress edits the user hadn't saved yet
  // — the "content reverts to default" bug.
  useEffect(() => {
    if (dirty) return;
    const nextEn = override?.en ?? defaults.en ?? "";
    const nextPt = override?.pt ?? defaults.pt ?? "";
    const nextEs = override?.es ?? defaults.es ?? "";
    if (nextEn !== en) setEn(nextEn);
    if (nextPt !== pt) setPt(nextPt);
    if (nextEs !== es) setEs(nextEs);
  }, [override?.en, override?.pt, override?.es, defaults.en, defaults.pt, defaults.es, en, pt, es]);

  const change = (setter) => (e) => { setter(e.target.value); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      // Only send fields that differ from the compiled default; empty a field to remove that language's override
      const payload = {
        en: en === defaults.en ? null : en,
        pt: pt === defaults.pt ? null : pt,
        es: es === defaults.es ? null : es,
      };
      const result = await adminSaveContentKey(k, payload);
      setDirty(false);
      setFlash("Saved");
      setTimeout(() => setFlash(""), 1500);
      // Hand the exact saved row back to the parent so it can update
      // only this key in its `overrides` map — no wholesale reload that
      // would clobber sibling rows' unsaved edits.
      onSaved?.(k, result);
    } catch (_e) {
      setFlash("Failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setEn(defaults.en || "");
    setPt(defaults.pt || "");
    setEs(defaults.es || "");
    setSaving(true);
    try {
      const result = await adminSaveContentKey(k, { en: null, pt: null, es: null });
      setFlash("Reset");
      setTimeout(() => setFlash(""), 1500);
      onSaved?.(k, result);
    } catch (_e) {
      setFlash("Failed");
    } finally {
      setSaving(false);
      setDirty(false);
    }
  };

  const hasOverride = Boolean(override);
  const cellClass =
    "w-full px-2 py-1.5 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm";

  return (
    <div className="py-3 border-b border-[#F1DCC5] last:border-0" data-testid={`content-row-${k}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="font-mono text-[11px] text-[#8A7568] truncate">
          {k}
          {hasOverride && (
            <span className="ml-2 inline-block px-1.5 py-0.5 bg-[#F4CB47] text-[#241C18] text-[9px] rounded font-bold">
              CUSTOMIZED
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs">
          {flash && <span className="text-[#4a7a2c]">{flash}</span>}
          {hasOverride && (
            <button
              type="button"
              onClick={reset}
              disabled={saving}
              className="text-[#8A7568] hover:text-red-600 underline underline-offset-2"
            >
              Reset
            </button>
          )}
          <button
            type="button"
            data-testid={`content-save-${k}`}
            onClick={save}
            disabled={saving || !dirty}
            className={`px-3 py-1 rounded-full font-badge text-[10px] tracking-[0.18em] transition ${
              dirty
                ? "bg-[var(--gl-orange)] text-[#241C18] hover:bg-[var(--gl-orange-hover)]"
                : "bg-[#F1DCC5] text-[#8A7568] cursor-not-allowed"
            }`}
          >
            {saving ? "…" : "Save"}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {[["EN", en, setEn], ["PT", pt, setPt], ["ES", es, setEs]].map(([label, val, setter]) => (
          <label key={label} className="block">
            <span className="text-[9px] tracking-[0.22em] text-[#8A7568] font-bold">{label}</span>
            {(val || "").length > 60 ? (
              <textarea
                data-testid={`content-input-${k}-${label.toLowerCase()}`}
                value={val}
                onChange={change(setter)}
                rows={2}
                className={`${cellClass} resize-none`}
              />
            ) : (
              <input
                data-testid={`content-input-${k}-${label.toLowerCase()}`}
                value={val}
                onChange={change(setter)}
                className={cellClass}
              />
            )}
          </label>
        ))}
      </div>
    </div>
  );
};

// ─────────────────────── Main editor ─────────────────────────────

export const ContentEditor = () => {
  const { refresh: refreshSiteContent } = useSiteContent();
  const [overrides, setOverrides] = useState({});
  const [settings, setSettings] = useState({ hero_image_url: "", hero_image_position: "40% 45%", instagram_gallery: [] });
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState(""); // "" = all
  const [loading, setLoading] = useState(true);
  const [showOnlyCustomized, setShowOnlyCustomized] = useState(false);

  const reload = async () => {
    try {
      const data = await adminListContent();
      setOverrides(data.overrides || {});
      setSettings({
        hero_image_url: data.settings?.hero_image_url || "",
        hero_image_position: data.settings?.hero_image_position || "40% 45%",
        hero_image_position_mobile: data.settings?.hero_image_position_mobile || "75% 45%",
        hero_image_scale: typeof data.settings?.hero_image_scale === "number" ? data.settings.hero_image_scale : 1.0,
        hero_image_scale_mobile: typeof data.settings?.hero_image_scale_mobile === "number" ? data.settings.hero_image_scale_mobile : 1.0,
        instagram_gallery: Array.isArray(data.settings?.instagram_gallery)
          ? data.settings.instagram_gallery
          : [],
        category_images: data.settings?.category_images && typeof data.settings.category_images === "object"
          ? data.settings.category_images
          : {},
        announcement_bar_enabled: Boolean(data.settings?.announcement_bar_enabled),
        announcement_bar_msg2_enabled: data.settings?.announcement_bar_msg2_enabled !== false,
        announcement_bar_text: data.settings?.announcement_bar_text && typeof data.settings.announcement_bar_text === "object"
          ? data.settings.announcement_bar_text
          : { en: "", pt: "", es: "" },
        free_shipping_threshold: typeof data.settings?.free_shipping_threshold === "number"
          ? data.settings.free_shipping_threshold
          : 150,
        allowed_shipping_countries: Array.isArray(data.settings?.allowed_shipping_countries)
          ? data.settings.allowed_shipping_countries
          : ["PT", "ES"],
        supported_shipping_countries: Array.isArray(data.settings?.supported_shipping_countries)
          ? data.settings.supported_shipping_countries
          : [],
        hero_title_scale: typeof data.settings?.hero_title_scale === "number"
          ? data.settings.hero_title_scale
          : 1.0,
        hero_title_color: typeof data.settings?.hero_title_color === "string" ? data.settings.hero_title_color : "",
        hero_cta_color: typeof data.settings?.hero_cta_color === "string" ? data.settings.hero_cta_color : "",
        hero_subtitle_enabled: data.settings?.hero_subtitle_enabled !== false,
        collection_title_enabled: data.settings?.collection_title_enabled !== false,
        maintenance_mode: Boolean(data.settings?.maintenance_mode),
        maintenance_message: data.settings?.maintenance_message && typeof data.settings.maintenance_message === "object"
          ? data.settings.maintenance_message
          : { en: "", pt: "", es: "" },
        show_stock_left: data.settings?.show_stock_left !== false,
        stock_left_threshold: typeof data.settings?.stock_left_threshold === "number" ? data.settings.stock_left_threshold : 3,
        trending_enabled: data.settings?.trending_enabled !== false,
      });
      refreshSiteContent();  // update live storefront view
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  // Surgical local update after a single translation key is saved: mutate
  // only that entry in the `overrides` map (or delete it on reset) instead
  // of refetching the whole content bundle. A full reload would rebuild
  // the entire overrides object → every KeyRow gets a new `override`
  // prop identity → any row with an unsaved edit-in-progress would be
  // reset by its sync effect, which manifested to the admin as "content
  // I typed reverted to the default". Storefront cache still gets
  // refreshed so live pages reflect the change.
  const handleKeySaved = (k, result) => {
    setOverrides((prev) => {
      const next = { ...prev };
      if (result?.deleted) {
        delete next[k];
      } else if (result) {
        next[k] = { en: result.en ?? null, pt: result.pt ?? null, es: result.es ?? null };
      }
      return next;
    });
    refreshSiteContent();
  };

  // Build the full key list from compiled defaults; group + filter for display
  const allKeys = useMemo(() => {
    const set = new Set([
      ...Object.keys(TRANSLATIONS.en || {}),
      ...Object.keys(TRANSLATIONS.pt || {}),
      ...Object.keys(TRANSLATIONS.es || {}),
    ]);
    return Array.from(set).sort();
  }, []);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const map = new Map();  // groupLabel → keys[]
    for (const k of allKeys) {
      const defs = {
        en: TRANSLATIONS.en?.[k] || "",
        pt: TRANSLATIONS.pt?.[k] || "",
        es: TRANSLATIONS.es?.[k] || "",
      };
      const hay = `${k} ${defs.en} ${defs.pt} ${defs.es}`.toLowerCase();
      if (q && !hay.includes(q)) continue;
      if (showOnlyCustomized && !overrides[k]) continue;
      const g = groupForKey(k);
      if (group && g.label !== group) continue;
      const list = map.get(g.label) || [];
      list.push(k);
      map.set(g.label, list);
    }
    return map;
  }, [allKeys, overrides, query, group, showOnlyCustomized]);

  const totalShown = useMemo(() => {
    let n = 0;
    for (const list of grouped.values()) n += list.length;
    return n;
  }, [grouped]);
  const totalCustomized = Object.keys(overrides).length;

  return (
    <div className="p-6 md:p-10">
      <AnnouncementBarEditor
        initial={{
          enabled: settings.announcement_bar_enabled,
          msg2Enabled: settings.announcement_bar_msg2_enabled,
          text: settings.announcement_bar_text,
        }}
        initialThreshold={settings.free_shipping_threshold}
        onSaved={reload}
      />
      <ShippingCountriesEditor
        initialSelected={settings.allowed_shipping_countries}
        supported={settings.supported_shipping_countries}
        onSaved={reload}
      />
      <HeroBannerEditor
        initialImageUrl={settings.hero_image_url}
        initialPos={settings.hero_image_position}
        initialPosMobile={settings.hero_image_position_mobile}
        initialScale={settings.hero_image_scale}
        initialScaleMobile={settings.hero_image_scale_mobile}
        initialTitleScale={settings.hero_title_scale}
        initialTitleColor={settings.hero_title_color}
        initialCtaColor={settings.hero_cta_color}
        initialSubtitleEnabled={settings.hero_subtitle_enabled}
        initialCollectionTitleEnabled={settings.collection_title_enabled}
        onSaved={reload}
      />
      <MaintenanceEditor
        initial={settings.maintenance_mode}
        initialMessage={settings.maintenance_message}
        onSaved={reload}
      />
      <StorefrontSwitchesEditor
        initialShowStockLeft={settings.show_stock_left}
        initialThreshold={settings.stock_left_threshold}
        initialTrendingEnabled={settings.trending_enabled}
        onSaved={reload}
      />
      <InstagramGalleryEditor
        initial={settings.instagram_gallery}
        onSaved={reload}
      />
      <CategoryImagesEditor
        initial={settings.category_images}
        onSaved={reload}
      />

      {/* Search + filters */}
      <div className="bg-white border border-[#F1DCC5] rounded-2xl p-4 mb-4 flex flex-wrap gap-3 items-center">
        <input
          data-testid="admin-content-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search text or key…"
          className="flex-1 min-w-[240px] px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm"
        />
        <select
          data-testid="admin-content-group"
          value={group}
          onChange={(e) => setGroup(e.target.value)}
          className="px-3 py-2 border border-[#F1DCC5] bg-white rounded text-sm outline-none focus:border-[var(--gl-orange)]"
        >
          <option value="">All sections</option>
          {GROUPS.map((g) => (
            <option key={g.label} value={g.label}>{g.label}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-xs text-[#241C18]">
          <input
            type="checkbox"
            data-testid="admin-content-customized-only"
            checked={showOnlyCustomized}
            onChange={(e) => setShowOnlyCustomized(e.target.checked)}
          />
          Customized only
        </label>
        <div className="text-xs text-[#8A7568]">
          {totalShown} shown · {totalCustomized} customized
        </div>
      </div>

      {loading ? (
        <div className="text-[#8A7568] p-4">Loading…</div>
      ) : totalShown === 0 ? (
        <div className="bg-white border border-[#F1DCC5] rounded-2xl p-10 text-center text-[#8A7568]">
          No matching text keys.
        </div>
      ) : (
        <div className="space-y-6">
          {Array.from(grouped.entries()).map(([groupLabel, keys]) => (
            <section key={groupLabel} className="bg-white border border-[#F1DCC5] rounded-2xl overflow-hidden">
              <header
                className="px-4 py-3 flex items-center justify-between"
                style={{ background: GROUPS.find((g) => g.label === groupLabel)?.accent + "40" || "#F1DCC5" }}
              >
                <div className="font-badge text-[11px] tracking-[0.24em] text-[#241C18]">{groupLabel}</div>
                <div className="text-xs text-[#8A7568]">{keys.length}</div>
              </header>
              {groupLabel === "SEO — Google & browser tab" && (
                <div
                  data-testid="admin-content-seo-note"
                  className="mx-4 mt-3 mb-1 rounded-lg border border-[#F1DCC5] bg-[#FFE9D6]/40 p-3 text-xs leading-relaxed text-[#241C18]"
                >
                  <div className="font-badge tracking-[0.18em] text-[10px] text-[#8A7568] mb-1">
                    HOW GOOGLE PICKS THESE UP
                  </div>
                  Saved edits are pushed to Facebook, Twitter, WhatsApp and Google&nbsp;
                  <span className="whitespace-nowrap">first-pass crawlers</span> instantly (via our edge SEO rewriter).
                  Google Search results usually reflect changes in 3–14 days once Googlebot next crawls the page.
                  To speed it up: open{" "}
                  <a
                    href="https://search.google.com/search-console"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2 hover:text-[var(--gl-orange-hover)]"
                  >
                    Google Search Console
                  </a>
                  {" "}→ URL Inspection → paste the page URL → click <em>Request Indexing</em>.
                </div>
              )}
              <div className="px-4 py-2">
                {keys.map((k) => {
                  const defaults = {
                    en: TRANSLATIONS.en?.[k] || "",
                    pt: TRANSLATIONS.pt?.[k] || "",
                    es: TRANSLATIONS.es?.[k] || "",
                  };
                  return (
                    <KeyRow
                      key={k}
                      k={k}
                      defaults={defaults}
                      override={overrides[k]}
                      onSaved={handleKeySaved}
                    />
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};
